import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';
import { DigitalNutritionURL, NiRestURL, openEasyRestSmartstore } from '../../../utils/localStorage';
import { Icon } from './Icon';
import { SoundPillMark } from './SoundPillMark';
import { LanguageMenu } from './LanguageMenu';
import { AuthStateContext } from '../../../contexts/AuthContext';
import { useSearchOverlay } from '../../../contexts/SearchOverlayContext';

type TopBarProps = {
  showNavLinks?: boolean;
};

const topbarIconBtn = 'soundpill-draft-icon-btn';
const topbarLangBtn = 'soundpill-draft-icon-btn soundpill-draft-icon-btn--language';

const navLinkClass =
  'soundpill-draft-nav-link text-[14px] leading-[1.1] font-extrabold not-italic no-underline transition-colors whitespace-nowrap';

const navLinkStyle = { letterSpacing: '-0.48px' } as const;

const dividerClass = 'flex w-[2px] h-3 mx-5 bg-[#E5E5E5] shrink-0';

/** Draft TopBar.tsx — className 은 draft-header.css 가 담당 */
export function TopBar({ showNavLinks = true }: TopBarProps) {
  const { t, i18n } = useTranslation();
  const { openSearch } = useSearchOverlay();
  const { user, isAuthentication } = useContext(AuthStateContext);
  const localeShort =
    i18n.language === 'en' ? t('layout.localeEnShort') : t('layout.localeKoShort');

  return (
    <header className="soundpill-draft-topbar glass-panel">
      <div className="flex items-center min-w-0 shrink-0">
        <Link to="/app/home" className="flex items-center min-w-0 shrink-0 no-underline">
          <SoundPillMark />
          <span
            className="ml-[11px] mr-[17px] text-[20px] leading-none font-semibold whitespace-nowrap not-italic"
            style={{ letterSpacing: '-0.72px', color: '#E5E2E3' }}
          >
            {t('header.soundpill')}
          </span>
        </Link>

        {showNavLinks && (
          <nav className="hidden lg:flex items-center">
            <span className={dividerClass} aria-hidden />
            <a
              href={NiRestURL}
              className={navLinkClass}
              style={navLinkStyle}
              onClick={(e) => {
                e.preventDefault();
                openEasyRestSmartstore();
              }}
            >
              {t('layout.easyRest')}
            </a>
            <span className={dividerClass} aria-hidden />
            <a
              href={DigitalNutritionURL}
              target="_blank"
              rel="noopener noreferrer"
              className={navLinkClass}
              style={navLinkStyle}
            >
              {t('layout.digitalNutrition')}
            </a>
          </nav>
        )}
      </div>

      <div className="flex items-center gap-0.5 sm:gap-1 ml-auto shrink-0 h-full">
        <div className="flex items-center">
          <button
            type="button"
            onClick={openSearch}
            className={topbarIconBtn}
            aria-label={t('common.search')}
          >
            <Icon name="search" />
          </button>
          <LanguageMenu className={topbarLangBtn} />
        </div>

        <div className="hidden sm:flex items-center gap-2 sm:gap-3 h-9 px-1 sm:px-2">
          <span className="w-px h-4 bg-white/10 shrink-0" aria-hidden />
          <span className="soundpill-draft-locale-short">{localeShort}</span>
          {!isAuthentication ? (
            <Link to="/auth" className="soundpill-draft-login-btn">
              {t('web.login')}
            </Link>
          ) : user?.nickName ? (
            <>
              <span className="w-px h-4 bg-white/10 shrink-0" aria-hidden />
              <Link to="/app/profile" className="soundpill-draft-nickname">
                {user.nickName}
              </Link>
            </>
          ) : null}
        </div>
      </div>
    </header>
  );
}
